/**
 * HistoryWidget
 *
 * Dashboard widget that displays fund statistics and history.
 * Self-contained - fetches its own data.
 */
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Box } from '@mui/material'

import { Button, CardBox, Container, Stack, Text } from '../..'
import { queries } from '../../../graphql'
import { toAmountString } from '../../../utils'

export function HistoryWidget() {
  const { fundId } = useParams()

  const { data: { me: { fund = {} } = {} } = {}, loading } = useQuery(queries.funds.myFund, {
    skip: !fundId,
    variables: { fundId: parseInt(fundId, 10) },
  })

  const {
    netTotalGifts,
    netTotalGiftsYTD,
    netTotalGiftsPreviousYear,
    netTotalContributionsGiving,
    netTotalContributionsInvestment,
    netTotalContributionsGivingYTD,
    netTotalContributionsGivingPreviousYear,
    netTotalContributionsInvestmentYTD,
    uniqueCharitiesCount,
  } = fund

  if (loading) {
    return (
      <CardBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text.Body>Loading...</Text.Body>
      </CardBox>
    )
  }

  if (!fund) return null

  return (
    <Box sx={{ px: 3 }}>
      <Stack>
        <Text.H2>History</Text.H2>
        <CardBox>
          {/* Total Contributions */}
          <Container
            sx={{
              border: '2px solid',
              borderColor: 'primary.light',
              borderRadius: 2,
              padding: 2,
              bgcolor: 'grey.100',
            }}
          >
            <Stack spacing={1}>
              <Text.Bold>Total Fund Contributions</Text.Bold>
              <Text.AltBody>
                Year to Date (YTD):{' '}
                <b>{toAmountString(netTotalContributionsGivingYTD + netTotalContributionsInvestmentYTD)}</b>
              </Text.AltBody>
              <Text.AltBody>
                Previous Year:{' '}
                <b>{toAmountString(netTotalContributionsGivingPreviousYear + netTotalContributionsInvestmentYTD)}</b>
              </Text.AltBody>
              <Text.AltBody>
                Lifetime: <b>{toAmountString(netTotalContributionsGiving + netTotalContributionsInvestment)}</b>
              </Text.AltBody>
              <Button size="small" to="contribute">
                Make a Contribution
              </Button>
            </Stack>
          </Container>

          {/* Total Gifts */}
          <Container
            sx={{
              border: '2px solid',
              borderColor: 'primary.light',
              borderRadius: 2,
              padding: 2,
              bgcolor: 'grey.100',
            }}
          >
            <Stack spacing={1}>
              <Text.Bold>Total Gifts</Text.Bold>
              <Text.AltBody>
                Year to Date (YTD): <b>{toAmountString(netTotalGiftsYTD)}</b>
              </Text.AltBody>
              <Text.AltBody>
                Previous Year: <b>{toAmountString(netTotalGiftsPreviousYear)}</b>
              </Text.AltBody>
              <Text.AltBody>
                Lifetime: <b>{toAmountString(netTotalGifts)}</b>
              </Text.AltBody>
              <Text.AltBody>
                Number of Charities Gifted To: <b>{uniqueCharitiesCount}</b>
              </Text.AltBody>
              <Button size="small" to="grant">
                Give to Charity
              </Button>
            </Stack>
          </Container>
        </CardBox>
      </Stack>
    </Box>
  )
}
